import { useState, useEffect } from 'react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import TeesSubNav from '../components/TeesSubNav';
import TeesProductCard from '../components/TeesProductCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GENDER_LABELS = {
  men: 'Men',
  women: 'Women',
  kids: 'Kids'
};

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('tees') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

const GARMENT_CATEGORIES = [
  { id: 'short-sleeve', label: 'Short Sleeve' },
  { id: 'long-sleeve', label: 'Long Sleeve' },
  { id: 'new-designs', label: 'New Designs' },
  { id: 'caps', label: 'Headwear' },
  { id: 'hoodies', label: 'Hoodies' },
  { id: 'tank-tops', label: 'Tank Tops' }
];

function TeesGender({ gender }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const { itemCount, openCart } = useCart();

  const genderLabel = GENDER_LABELS[gender] || gender;

  useEffect(() => {
    setActiveCategory('all');
    fetchProducts();
  }, [gender]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tees`, { params: { gender } });
      setProducts(response.data.filter(p => p.active));
    } catch (error) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const visibleProducts = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory);

  const categoriesWithProducts = GARMENT_CATEGORIES.filter(cat =>
    products.some(p => p.category === cat.id)
  );

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      <TeesSubNav />

      {/* Title */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px 20px' }}>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '36px',
          margin: '0 0 10px',
          color: '#1a1a1a'
        }}>
          {genderLabel}
        </h1>
        <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>
          {products.length > 0
            ? `${products.length} design${products.length !== 1 ? 's' : ''} available`
            : 'Browse our collection'}
        </p>
      </div>

      {/* Garment-type filter chips */}
      {categoriesWithProducts.length > 0 && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px 20px',
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveCategory('all')}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid #ddd',
              background: activeCategory === 'all' ? '#1a1a1a' : '#fff',
              color: activeCategory === 'all' ? '#fff' : '#333',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            All
          </button>
          {categoriesWithProducts.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: '1px solid #ddd',
                background: activeCategory === cat.id ? '#1a1a1a' : '#fff',
                color: activeCategory === cat.id ? '#fff' : '#333',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Product Grid */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 20px 60px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="loading-spinner" />
          </div>
        ) : visibleProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ fontSize: '18px', color: '#666' }}>
              No designs here yet. Check back soon!
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '30px'
          }}>
            {visibleProducts.map(product => (
              <TeesProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

export default TeesGender;
